import React, { useCallback, useMemo, useContext, useState } from 'react';
import _ from 'lodash';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import { translate as t } from 'hooks/useTranslations';
import GridSection from 'components/_layouts/GridSection';
import { useEisRules } from '../_data_sources/useEisRules';
import { Box, Button, Typography, CircularProgress, Stack } from '@mui/material';
import PageToolbar from '_lex/_atoms/PageToolbar';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { useNavigate } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import { ActionMenu } from 'components/_atoms';
import useUrlParams from 'hooks/useUrlParams';
import { useConfirm } from 'material-ui-confirm';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import useManageEisRules from '../_data_sources/useManageEisRules';

export type EisRulesListParams = {
  search?: string | null;
  versionStatus?: string | null;
};

const defaultParams: EisRulesListParams = {
  search: null,
  versionStatus: null,
};

const EarlyInterventionSystemList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams);

  const updateParams = useCallback(
    (updatedParams: EisRulesListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams],
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    return {
      ...cleanParams,
      ...(currentOrganizationId ? { organizationId: currentOrganizationId } : {}),
    };
  }, [params, currentOrganizationId]);

  const isFiltered = !_.isEmpty(queryParams);

  const eisRulesQuery = useEisRules(queryParams);
  const allRules = eisRulesQuery.data?.pages.flatMap((page) => page.content) || [];
  const totalCount = eisRulesQuery.data?.pages[0]?.count || 0;
  const { isLoading, isError, error } = eisRulesQuery;

  const canManageEisRules = hasPermission('manageFieldBank', currentOrganizationId || '');

  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { deleteRule } = useManageEisRules();
  const [loadingRuleId, setLoadingRuleId] = useState<number | null>(null);

  const onSearchChange = useCallback(
    (val: string) => {
      updateParams({ search: val?.trim() || null });
    },
    [updateParams],
  );

  const formatTemplateSource = (rule: any) => {
    if (!rule.template) {
      return '-';
    }
    return <Typography variant='body1'>{rule.template.name}</Typography>;
  };

  const formatThreshold = (rule: any) => {
    if (
      !rule.activeVersion ||
      rule.activeVersion.thresholdPoints === null ||
      rule.activeVersion.thresholdPoints === undefined
    ) {
      return '-';
    }
    return `${rule.activeVersion.thresholdPoints} pts`;
  };

  const formatTimeWindow = (rule: any) => {
    if (!rule.activeVersion || !rule.activeVersion.windowType) {
      return '-';
    }
    if (rule.activeVersion.windowType === 'YearToDate') {
      return t('performanceReporting.eis.list.windowType.YTD');
    }
    if (rule.activeVersion.windowDays == null) {
      return '-';
    }
    return `${rule.activeVersion.windowDays} ${t('performanceReporting.eis.list.windowType.RollingDays')}`;
  };

  const formatFlags = (rule: any) => {
    if (rule.versionStatus === 'Draft') {
      return '-';
    }
    if (rule.flagsCount === undefined || rule.flagsCount === null) {
      return '-';
    }
    return rule.flagsCount.toString();
  };

  const handleDeleteRule = useCallback(
    async (ruleId: number, eisRuleVersionId: number) => {
      setLoadingRuleId(ruleId);
      await deleteRule(
        ruleId,
        eisRuleVersionId,
        () => {
          enqueueSnackbar(t('performanceReporting.eis.list.actions.deleteRuleSuccess'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          queryClient.invalidateQueries(['eisRules']);
        },
        () => {
          enqueueSnackbar(t('performanceReporting.eis.list.actions.deleteRuleError'), {
            variant: 'error',
            autoHideDuration: 3000,
          });
        },
      );
      setLoadingRuleId(null);
    },
    [deleteRule, queryClient],
  );

  const handleDeactivateRule = useCallback(
    async (ruleId: number, eisRuleVersionId: number) => {
      try {
        await confirm({
          title: t('performanceReporting.eis.list.actions.deactivateRuleConfirmTitle'),
          description: t('performanceReporting.eis.list.actions.deactivateRuleConfirmBody'),
          confirmationText: t('performanceReporting.eis.list.actions.deactivateRule'),
          cancellationText: t('common.cancel'),
          confirmationButtonProps: { variant: 'contained', color: 'error' },
          cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
        });
      } catch {
        return; // user cancelled
      }
      setLoadingRuleId(ruleId);
      await deleteRule(
        ruleId,
        eisRuleVersionId,
        () => {
          enqueueSnackbar(t('performanceReporting.eis.list.actions.deactivateRuleSuccess'), {
            variant: 'success',
            autoHideDuration: 3000,
          });
          queryClient.invalidateQueries(['eisRules']);
        },
        () => {
          enqueueSnackbar(t('performanceReporting.eis.list.actions.deactivateRuleError'), {
            variant: 'error',
            autoHideDuration: 3000,
          });
        },
      );
      setLoadingRuleId(null);
    },
    [confirm, deleteRule, queryClient],
  );

  const getActionMenuItems = useCallback(
    (ruleId: number, rule: any) => [
      {
        key: 'details',
        label: t('performanceReporting.eis.list.actions.viewDetails'),
        onClick: () => navigate(`details/${ruleId}`),
      },
      {
        key: 'configure-scores',
        label: t('performanceReporting.eis.list.actions.configureScores'),
        onClick: () => navigate(`configuration/${ruleId}`),
      },
      // ...(canManageEisRules && rule.versionStatus === 'Draft'
      //   ? [
      //       {
      //         key: 'edit',
      //         label: t('performanceReporting.eis.list.actions.edit'),
      //         onClick: () => navigate(`details/${ruleId}/edit`),
      //       },
      //     ]
      //   : []),
      ...(canManageEisRules && rule.versionStatus === 'Draft'
        ? [
            {
              key: 'delete-rule',
              label: t('performanceReporting.eis.list.actions.deleteRule'),
              onClick: () => handleDeleteRule(ruleId, rule.versionId),
            },
          ]
        : []),
      ...(canManageEisRules && rule.versionStatus === 'Active'
        ? [
            {
              key: 'deactivate-rule',
              label: t('performanceReporting.eis.list.actions.deactivateRule'),
              onClick: () => handleDeactivateRule(ruleId, rule.versionId),
            },
          ]
        : []),
    ],
    [canManageEisRules, navigate, handleDeleteRule, handleDeactivateRule],
  );

  const columns = useMemo(
    () => ({
      name: {
        header: t('performanceReporting.eis.list.columns.ruleName'),
        flex: 2,
        renderer: (params: any) => (
          <Typography
            variant='body1'
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={() => navigate(`details/${params.row.id}`)}
          >
            {params.row.name}
          </Typography>
        ),
      },
      templateSource: {
        header: t('performanceReporting.eis.list.columns.templateSource'),
        flex: 2,
        renderer: (params: any) => formatTemplateSource(params.row),
      },
      threshold: {
        header: t('performanceReporting.eis.list.columns.threshold'),
        flex: 1.5,
        renderer: (params: any) => <Typography variant='body1'>{formatThreshold(params.row)}</Typography>,
      },
      timeWindow: {
        header: t('performanceReporting.eis.list.columns.timeWindow'),
        flex: 1.5,
        renderer: (params: any) => <Typography variant='body1'>{formatTimeWindow(params.row)}</Typography>,
      },
      status: {
        header: t('performanceReporting.eis.list.columns.status'),
        flex: 1,
        renderer: (params: any) => {
          const status = params.row.versionStatus;
          const statusKey = status?.toUpperCase() || '';
          let color: 'success' | 'error' | 'default' = 'default';
          if (status === 'Active') color = 'success';
          else if (status === 'Draft') color = 'default';
          else if (status === 'Inactive') color = 'error';

          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {t(`performanceReporting.eis.list.status.${statusKey}`) || status}
            </PhxBadge>
          );
        },
      },
      flags: {
        header: t('performanceReporting.eis.list.columns.flags'),
        flex: 1,
        renderer: (params: any) => <Typography variant='body1'>{formatFlags(params.row)}</Typography>,
      },
      actions: {
        header: '',
        flex: 0.5,
        renderer: (params: any) => {
          if (loadingRuleId === params.row.id) {
            return (
              <Stack
                direction='row'
                alignItems='center'
              >
                <CircularProgress size={20} />
              </Stack>
            );
          }
          if (params.row.versionStatus === 'Inactive') {
            return null;
          }
          return (
            <ActionMenu
              id={`eis-rule-actions-${params.row.id}`}
              items={getActionMenuItems(params.row.id, params.row)}
            />
          );
        },
      },
    }),
    [t, navigate, getActionMenuItems, loadingRuleId],
  );

  return (
    <CommonLayout
      pageTitle={t('performanceReporting.eis.list.title')}
      headerProps={{
        title: t('performanceReporting.eis.list.title'),
        subtitle: t('performanceReporting.eis.list.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={null}
          rightItems={
            canManageEisRules ? (
              <Button
                variant='contained'
                color='primary'
                onClick={() => navigate('./new')}
              >
                {t('performanceReporting.eis.list.createRule')}
              </Button>
            ) : null
          }
        />
        {isLoading ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>Error loading EIS rules</Box>
        ) : allRules.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              height: '100%',
            }}
          >
            <Stack
              spacing={3}
              alignItems='center'
            >
              <Box
                component='img'
                src='/admin/img/performance-reporting/empty-eis.png'
                alt='No rules to display'
              />
              <Typography
                variant='h6'
                sx={{ fontWeight: 'bold' }}
              >
                {t('performanceReporting.eis.list.emptyState.title')}
              </Typography>
              <Typography
                variant='bodyMd'
                color='text.secondary'
              >
                {t('performanceReporting.eis.list.emptyState.subtitle')}
              </Typography>
            </Stack>
          </Box>
        ) : (
          <GridSection
            columns={columns}
            data={allRules}
            totalCount={totalCount}
            query={eisRulesQuery}
            gridKey={JSON.stringify(queryParams)}
          />
        )}
      </Box>
    </CommonLayout>
  );
};

export default EarlyInterventionSystemList;
